import { Metrics, QuarterlyReport, ChartData, ReportStatus, ReportResponse, ReportAnswer, UploadedFile, TemplateResponse } from '../types/reports';
import httpClient from './httpClient';

// Mock data
const mockMetrics: Metrics = {
  totalReports: 40,
  submittedReports: 35,
  completionRate: 87.5,
  testingCoverage: 92.3,
  activeClients: 15
};

const mockChartData: ChartData = {
  monthlyData: [
    { name: 'Jan', value: 65 },
    { name: 'Feb', value: 45 },
    { name: 'Mar', value: 58 },
    { name: 'Apr', value: 71 },
    { name: 'May', value: 89 },
    { name: 'Jun', value: 76 }
  ],
  pieData: [
    { name: 'Completed', value: 65, color: '#4CAF50' },
    { name: 'In Progress', value: 25, color: '#FFC107' },
    { name: 'Overdue', value: 10, color: '#F44336' }
  ],
  lineData: [
    { date: '2024-01-01', submissions: 4 },
    { date: '2024-01-02', submissions: 3 },
    { date: '2024-01-03', submissions: 7 },
    { date: '2024-01-04', submissions: 5 },
    { date: '2024-01-05', submissions: 8 },
    { date: '2024-01-06', submissions: 6 }
  ],
  // Skyway specific data
  clientData: [
    { month: 'Jan', clients: 2 },
    { month: 'Feb', clients: 3 },
    { month: 'Mar', clients: 5 },
    { month: 'Apr', clients: 4 },
    { month: 'May', clients: 7 },
    { month: 'Jun', clients: 9 }
  ],
  // DronePort specific data
  testingData: [
    { category: 'Security', value: 85 },
    { category: 'Performance', value: 73 },
    { category: 'Integration', value: 91 },
    { category: 'Load', value: 67 },
    { category: 'UI', value: 89 }
  ],
  securityData: [
    { date: '2024-01-01', coverage: 78 },
    { date: '2024-02-01', coverage: 82 },
    { date: '2024-03-01', coverage: 85 },
    { date: '2024-04-01', coverage: 91 },
    { date: '2024-05-01', coverage: 93 },
    { date: '2024-06-01', coverage: 95 }
  ]
};

// Helper function to simulate API delay
const mockDelay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const reportsApi = {
  getMetrics: async (): Promise<Metrics> => {
    // Simulate fast API response (300ms)
    await mockDelay(300);
    return mockMetrics;
  },

  getQuarterlyReports: async (): Promise<QuarterlyReport[]> => {
    try {
      const response = await httpClient.get('/v1/reports/recent');
      return response.data.map((report: any) => ({
        id: report.report_id,
        name: report.report_name,
        title: report.report_title ?? report.report_name,
        dueDate: report.due_date,
        status: report.submission_status.state.toLowerCase() as ReportStatus,
        isInProgress: report.is_in_progress
      }));
    } catch (error) {
      console.error('Error fetching quarterly reports:', error);
      throw error;
    }
  },

  getAllReports: async (): Promise<QuarterlyReport[]> => {
    try {
      const response = await httpClient.get('/v1/reports/');
      return response.data.map((report: any) => ({
        id: report.report_id,
        name: report.report_name,
        title: report.report_title ?? report.report_name,
        dueDate: report.due_date,
        status: report.submission_status.state.toLowerCase() as ReportStatus,
        isInProgress: report.is_in_progress
      }));
    } catch (error) {
      console.error('Error fetching all reports:', error);
      throw error;
    }
  },

  getAvailableYears: async (): Promise<string[]> => {
    try {
      const response = await httpClient.get('/api/v1/reports/years');
      return response.data;
    } catch (error) {
      console.error('Error fetching available years:', error);
      throw error;
    }
  },

  getReport: async (reportId: string): Promise<ReportResponse> => {
    try {
      const response = await httpClient.get(`/v1/reports/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching report:', error);
      throw error;
    }
  },

  getChartMetrics: async (): Promise<ChartData> => {
    // Simulate variable API response (600-1500ms)
    await mockDelay(600 + Math.random() * 900);
    return mockChartData;
  },

  saveAnswers: async (reportId: string, answers: ReportAnswer[]): Promise<ReportResponse> => {
    try {
      const response = await httpClient.patch(`/v1/reports/${reportId}`, { answers });
      return response.data;
    } catch (error) {
      console.error('Error saving draft:', error);
      throw error;
    }
  },

  updateReportStatus: async (
    reportId: string,
    status: ReportStatus,
    notes?: string
  ): Promise<ReportResponse> => {
    try {
      const payload: { status: ReportStatus; notes?: string } = { status };
      if (notes !== undefined) {
        payload.notes = notes;
      }
      const response = await httpClient.patch(`/v1/reports/${reportId}/status`, payload);
      return response.data;
    } catch (error) {
      console.error('Error updating report status:', error);
      throw error;
    }
  },

  // File operations
  getFiles: async (reportId: string): Promise<UploadedFile[]> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/`);
    return response.data.files.map((fileData: any) => ({
      id: fileData.file_id,
      name: fileData.filename.toLowerCase(),
      size: fileData.size,
      uploadDate: fileData.uploaded_at,
      url: fileData.gcs_url,
      type: '.' + fileData.filename.split('.').pop()?.toLowerCase(),
      contentType: fileData.content_type
    }));
  },

  uploadFile: async (
    reportId: string, 
    file: File,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<UploadedFile> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await httpClient.post(`/v1/reports/${reportId}/files/`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });

    const uploadedFileData = response.data;
    return {
      id: uploadedFileData.file_id,
      name: uploadedFileData.filename || file.name.toLowerCase(),
      size: uploadedFileData.size || file.size,
      uploadDate: uploadedFileData.uploaded_at || new Date().toISOString(),
      url: uploadedFileData.gcs_url || '#',
      type: '.' + file.name.split('.').pop()?.toLowerCase(),
      contentType: uploadedFileData.content_type
    };
  },

  deleteFile: async (reportId: string, fileId: string): Promise<void> => {
    await httpClient.delete(`/v1/reports/${reportId}/files/${fileId}`);
  },

  getFileDownloadUrl: async (reportId: string, fileId: string): Promise<{ download_url: string }> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/${fileId}`);
    return response.data;
  },

  // Template operations
  getTemplateFiles: async (reportId: string): Promise<TemplateResponse> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/template`);
    return response.data;
  },

  downloadTemplateFile: async (reportId: string, filename: string): Promise<Blob> => {
    const response = await httpClient.get(`/v1/reports/${reportId}/files/template/${filename}`, {
      responseType: 'blob'
    });
    return response.data;
  }
}; 